/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import isNil from 'lodash.isnil';
import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';

/**
 * Provides a utility to format a number with a specific number of
 * digits to the right of the decimal.
 *
 * ```typescript
 *
 *   import {toFixed} from '@ion/core/src/utils/';
 *
 *   toFixed(13.50000, 1) // 13.5
 *   toFixed(14.55000, 2) // 14.55
 *   toFixed(14.5567, 2)  // 14.56
 *
 * ```
 */
export const toFixed = (value: number | string, digits: number) => {
  const val = value && Number(value);
  return val && Number(val.toFixed(digits));
};

/**
 * Provides a utility to retrieves if a number is in range of two numbers
 *
 * ```typescript
 *
 *   import {inRange} from '@ion/core/src/utils/';
 *
 *   inRange(1, 1, 2) // true
 *   inRange(1, 1, 1) // true
 *   inRange(3, 1, 2) // false
 *
 * ```
 */
export const inRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max;
};

/**
 * Provides a utility to round a number to the specified precision (number of digits
 * to the right of the decimal point).
 *
 * ```typescript
 *
 *   import {roundToPrecision} from '@ion/core/src/utils/';
 *
 *   roundToPrecision(13.456, 2); // 13.46
 *   roundToPrecision(13.456)     // 13
 *
 * ```
 */
export const roundToPrecision = (num: number, precision: number = 0) => {
  return (precision > 0)
    ? Math.round(num * (10 ** precision)) / (10 ** precision)
    : Math.round(num);
};

/**
 * Provides a utility to match the Accumulator Conversion factor
 * to Add/Edit PEG Label / PEG Bhavior window.
 *
 * ```typescript
 *
 *   import {isValidateFactor} from '@ion/core/src/utils/';
 *
 *   isValidateFactor(.0625); //true
 *   isValidateFactor(/100)   //true
 *   isValidateFactor(abc)   //false
 *
 * ```
 */
export const isValidateFactor = (value: number | string): boolean => {
  const reg = /^[./*+-][\./]?(\d+(\.\d+)?)*$/;
  return Boolean(reg.test(value));
};

/**
 * Provides a utility to safely parse string to number;
 *
 * ```typescript
 *
 *   import {safeParseNumber} from '@ion/core/src/utils/';
 *
 *   safeParseNumber(.0625); //0.0625
 *   isValidateFactor('100')   //100
 *   isValidateFactor('abc')   //-1
 *
 * ```
 */
export const safeParseNumber = (value: any, fallback: string | number = -1) => {
  if (!isNil(value)) {
    return !isNumber(value) ? ((str) => {
      const idValue = parseInt(str);
      return isNaN(idValue) ? fallback : idValue;
    })(value) : value;
  }
  return fallback;
};

/**
 * Restrict the input field and only allow the numeric values.
 *
 * ```typescript
 *
 *   import {onlyNumbers} from '@ion/core/src/utils/';
 *
 *   onlyNumbers('123Abc') // 123
 *   onlyNumbers('123') // 123
 *   onlyNumbers('abc123%^&') // 123
 *
 * ```
 */
export const onlyNumbers = (value: any): number => {
  return value?.replace(/\D/g, '');
};

/**
 * Restrict the input field and only allow the numeric values with comma or spaces.
 *
 * ```typescript
 *
 *   import {onlyMultipleNumbers} from '@ion/core/src/utils/';
 *
 *   onlyMultipleNumbers('123hj jbj 122') // 123 122
 *   onlyMultipleNumbers('123 234') // 123 234
 *   onlyMultipleNumbers('123,234') // 123,234
 *   onlyMultipleNumbers('abc123%^& 231') // 123 231
 *
 * ```
 */
export const onlyMultipleNumbers = (value: any): number => {
  return value?.replace(/[^\d, ]/g, '');
};

/**
 * Validate if number is non zero.
 *
 * ```typescript
 *
 *   import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
 *
 *   isNonZeroNumber('123Abc') // false
 *   onlyNumbers(-123.2) // true
 *   onlyNumbers(0) // false
 *
 * ```
 */
export const isNonZeroNumber = (value: any): boolean => {
  return !Number.isNaN(value) &&
    !isString(value) &&
    Math.abs(value) > 0;
};

/**
 * Validate if a value contains only number.
 *
 * ```typescript
 *
 *   import {isANumber} from '@ion/core/src/utils/';
 *
 *   isANumber('00'); //true
 *   isANumber('1108'); //true
 *   isANumber('21_4'); //false
 *   isANumber('2abc'); //false
 *   isANumber('abcd'); //false
 *
 * ```
 */
export const isANumber = (value: string): boolean => {
  const reg = /^\d+$/;
  return Boolean(reg.test(value));
};

/**
 * Validate if a value contains only number & decimal.
 *
 * ```typescript
 *
 *   import {isDecimalNumber} from '@ion/core/src/utils/';
 *
 *   isANumber('00'); //true
 *   isANumber('1108'); //true
 *   isANumber('11.08'); //true
 *   isANumber('21_4'); //false
 *   isANumber('2abc'); //false
 *   isANumber('abcd'); //false
 *
 * ```
 */
export const isDecimalNumber = (value: string) => {
  const numericRegex = /^[+-]?\d+(\.\d+)?$/;
  return numericRegex?.test(value);
};
